import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { useUpdateLegalFeeItem } from '@/hooks/useLegalFeeStages';
import { editItemFormSchema, type EditItemFormData } from './legalFees.schema';
import type { LegalFeeItem } from '@/types/legalFees';

interface EditItemFormProps {
  item: LegalFeeItem;
  fixedAssetId: string;
  onSuccess: () => void;
}

export function EditItemForm({ item, fixedAssetId, onSuccess }: EditItemFormProps) {
  const updateItem = useUpdateLegalFeeItem();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<EditItemFormData>({
    resolver: zodResolver(editItemFormSchema),
    defaultValues: {
      title: item.title,
      currency: item.currency,
      amount: item.amount,
      description: item.description ?? '',
    },
  });

  const onSubmit = async (data: EditItemFormData) => {
    try {
      await updateItem.mutateAsync({
        itemId: item.id,
        fixedAssetId,
        ...data,
      });
      toast.success('Item updated successfully');
      onSuccess();
    } catch (error: any) {
      toast.error(error?.message ?? 'Failed to update item');
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      {/* Title */}
      <div className="space-y-2">
        <Label htmlFor="edit-item-title">Title *</Label>
        <Input
          id="edit-item-title"
          {...register('title')}
          aria-invalid={!!errors.title}
          aria-describedby={errors.title ? 'edit-item-title-error' : undefined}
        />
        {errors.title && (
          <p id="edit-item-title-error" className="text-sm text-destructive" role="alert">
            {errors.title.message}
          </p>
        )}
      </div>

      {/* Currency and Amount */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="edit-item-currency">Currency *</Label>
          <Input
            id="edit-item-currency"
            {...register('currency')}
            maxLength={3}
            placeholder="e.g. USD"
            aria-invalid={!!errors.currency}
            aria-describedby={errors.currency ? 'edit-item-currency-error' : undefined}
          />
          {errors.currency && (
            <p id="edit-item-currency-error" className="text-sm text-destructive" role="alert">
              {errors.currency.message}
            </p>
          )}
        </div>
        <div className="space-y-2">
          <Label htmlFor="edit-item-amount">Amount *</Label>
          <Input
            id="edit-item-amount"
            type="number"
            step="0.01"
            min="0"
            {...register('amount', { valueAsNumber: true })}
            aria-invalid={!!errors.amount}
            aria-describedby={errors.amount ? 'edit-item-amount-error' : undefined}
          />
          {errors.amount && (
            <p id="edit-item-amount-error" className="text-sm text-destructive" role="alert">
              {errors.amount.message}
            </p>
          )}
        </div>
      </div>

      {/* Description */}
      <div className="space-y-2">
        <Label htmlFor="edit-item-description">Description (Optional)</Label>
        <Textarea
          id="edit-item-description"
          {...register('description')}
          placeholder="Optional description…"
          rows={3}
          maxLength={1000}
        />
        {errors.description && (
          <p className="text-sm text-destructive" role="alert">
            {errors.description.message}
          </p>
        )}
      </div>

      <DialogFooter>
        <Button type="submit" disabled={isSubmitting || updateItem.isPending}>
          {isSubmitting || updateItem.isPending ? 'Saving…' : 'Save Changes'}
        </Button>
      </DialogFooter>
    </form>
  );
}
